import type { SearchResponse } from '@algolia/client-search';
import { useLocalStorage } from '@vueuse/core';
import { computed, MaybeRefOrGetter, ref, toValue } from 'vue';

import { AlgoliaVariant, BaseAlgoliaVariant, browseProducts, searchProducts } from '@/api/algolia';
import { getBundleParentLookup } from '@/api/customizers/bundleParentLookup';
import { ENCODING_DELIMITER } from '@/lib/customizers/bundle';
import { sendProductsSearched } from '@/utils/analytics/productSearched';
import { NutsProductProjectionDisplayVariant } from '@/utils/productProjection';

type Facet = 'Product.facets.value' | 'Product.tags';

export interface Filter {
  count: number;
  facet: string;
  option: string;
}

interface SavedFiltersState {
  categoryFilters: Filter[];
  dietaryFilters: Filter[];
  flavorFilters: Filter[];
  lifestyleFilters: Filter[];
  quizTaken: boolean;
}

export type FilterOptions = Record<Facet, Filter[]>;

export interface SearchOptions {
  preferences?: Filter[];
  selectedFilters?: Filter[];
}

export const DIETARY_RESTRICTIONS: {
  displayText: string;
  filter: Filter;
}[] = [
  {
    displayText: 'Gluten Free',
    filter: {
      count: 0,
      facet: 'Product.tags',
      option: 'gluten-free',
    },
  },
  {
    displayText: 'Organic',
    filter: {
      count: 0,
      facet: 'Product.tags',
      option: 'organic',
    },
  },
  {
    displayText: 'Sugar Free',
    filter: {
      count: 0,
      facet: 'Product.tags',
      option: 'sugar-free',
    },
  },
  {
    displayText: 'Kosher',
    filter: {
      count: 0,
      facet: 'Product.tags',
      option: 'kosher',
    },
  },
];

export function facetFilter(filter: Filter) {
  return `${filter.facet}:${filter.option}`;
}

interface Options {
  asSubscription?: MaybeRefOrGetter<boolean | undefined>;
}

export function useBundleCustomizer(options?: Options) {
  const subscriptionMode = ref(toValue(options?.asSubscription) ?? false);

  const savedFilters = useLocalStorage<SavedFiltersState>('csbSavedFilters', {
    categoryFilters: [],
    dietaryFilters: [],
    flavorFilters: [],
    lifestyleFilters: [],
    quizTaken: false,
  });

  const bundleParentLookup = ref<NutsProductProjectionDisplayVariant>();
  const loadBundleParentLookup = async () => {
    bundleParentLookup.value = await getBundleParentLookup();
  };

  const baseCatalog = ref<AlgoliaVariant[]>([]);
  async function loadBaseCatalog(encodedSkus?: string) {
    const primaryFilters = ['traits:customAvailability.CSB'];
    const facetFilters: string[][] = [primaryFilters];

    if (subscriptionMode.value) {
      facetFilters.push(['autoDeliveryEligible:true']);
    }

    const skuList = (encodedSkus ?? '').split(ENCODING_DELIMITER).filter(Boolean);
    if (skuList.length) {
      primaryFilters.push(...skuList.map((sku) => `sku:${sku}`));
    }

    const results = await browseProducts({
      /**
       * TODO: consider `distinct: 2` to support a combination of entries.
       *
       * Example: 5455-06 is currently the main CSB variant, but 5455-01 would be the match
       * for a `traits:searchable` extended search if users look up "gummy bears".
       *
       * That trait only gets set for master variants that are not hidden, so we may need to make
       * sure a resolved product does not fully override the CSB variant. While it's an edge case,
       * both variants are valid selections as part of this "extended search" feature.
       */
      facetFilters,
    });

    baseCatalog.value = results;
  }

  const searchResult = ref<SearchResponse<BaseAlgoliaVariant>>();
  const extendedSearchResult = ref<SearchResponse<BaseAlgoliaVariant>>();

  const filters = computed<FilterOptions>(() => {
    const activeFacets = searchResult.value?.facets;
    if (!activeFacets) {
      return {
        'Product.facets.value': [],
        'Product.tags': [],
      };
    }

    return Object.fromEntries(
      Object.entries(activeFacets).map(([facet, facetOptions]) => [
        facet,
        Object.entries(facetOptions).map(([option, count]) => ({
          count,
          facet,
          option,
        })),
      ]),
    ) as FilterOptions;
  });

  async function search(queryOptions: MaybeRefOrGetter<SearchOptions> = {}) {
    const searchOptions = toValue(queryOptions);

    const facetFilters = [
      'outOfStock:false',
      [...(searchOptions.selectedFilters?.map(({ facet, option }) => `${facet}:${option}`) ?? [])],
      [...(searchOptions.preferences?.map(({ facet, option }) => `${facet}:${option}`) ?? [])],
    ];

    if (subscriptionMode.value) {
      facetFilters.push(['autoDeliveryEligible:true']);
    }

    const results = await searchProducts({
      facetFilters,
      facets: [
        'outOfStock',
        'Product.customizerCategoryNames.lvl0',
        'Product.facets.flavor',
        'Product.facets.value',
        'Product.tags',
      ],
      filters: 'traits:customAvailability.CSB',
      hitsPerPage: 200,
    });

    searchResult.value = results;
    return results;
  }

  async function extendedSearch({
    pageSection,
    query,
  }: {
    pageSection?: 'Custom Snack Box Builder Search' | 'Custom Snack Box Subscription Portal';
    query: string;
  }) {
    if (!query) {
      extendedSearchResult.value = undefined;
      return;
    }
    const facetFilters = [
      'outOfStock:false',
      'Product.merchandisingCategory:-Gifts',
      'Product.requiresCustomization:false',
    ];

    if (subscriptionMode.value) {
      facetFilters.push('autoDeliveryEligible:true');
    }

    const results = await searchProducts({
      facetFilters,
      facets: ['outOfStock', 'Product.merchandisingCategory:', 'Product.requiresCustomization'],
      filters: 'traits:searchable',
      hitsPerPage: 20,
      query,
      ruleContexts: ['CSB-extended-search'],
    });

    sendProductsSearched({ pageSection, query });

    extendedSearchResult.value = results;
  }

  return {
    bundleParentLookup,
    baseCatalog,
    extendedSearch,
    extendedSearchResult,
    filters,
    loadBundleParentLookup,
    loadBaseCatalog,
    savedFilters,
    search,
    searchResult,
    subscriptionMode,
  };
}
